import AppLayout from '../components/AppLayout';
import { Bell, Shield, DollarSign, Settings as SettingsIcon } from 'lucide-react';

export default function Settings() {
  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
          <p className="text-slate-400">Configure your Vigilant preferences</p>
        </div>

        {/* Detection Thresholds */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Detection Thresholds</h2>
              <p className="text-sm text-slate-400">Configure when to flag issues</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Overcharge Threshold (%)
              </label>
              <input
                type="number"
                defaultValue={20}
                className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50"
              />
              <p className="text-xs text-slate-500 mt-1">Flag invoices exceeding average by this percentage</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                High Amount Alert ($)
              </label>
              <input
                type="number"
                defaultValue={5000}
                className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50"
              />
              <p className="text-xs text-slate-500 mt-1">Alert when invoice exceeds this amount</p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Notifications</h2>
              <p className="text-sm text-slate-400">Manage alert preferences</p>
            </div>
          </div>

          <div className="space-y-4">
            {[
              { label: 'High severity findings', description: 'Get notified of critical issues immediately' },
              { label: 'Daily summary report', description: 'Receive daily digest of activity' },
              { label: 'Recovery completions', description: 'Alert when funds are recovered' },
              { label: 'Integration status changes', description: 'Notify on connection issues' },
            ].map((item) => (
              <label key={item.label} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked
                  className="mt-1 w-4 h-4 bg-slate-900/50 border border-slate-700/50 rounded text-cyan-500 focus:ring-cyan-500/50"
                />
                <div>
                  <p className="text-sm font-medium text-white">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Auto-Actions */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Automated Actions</h2>
              <p className="text-sm text-slate-400">Configure automatic responses</p>
            </div>
          </div>

          <div className="space-y-4">
            {[
              {
                label: 'Auto-place holds on duplicates',
                description: 'Automatically hold payments for duplicate invoices',
              },
              {
                label: 'Auto-initiate recovery',
                description: 'Start recovery process for high severity findings',
              },
              {
                label: 'Auto-categorize vendors',
                description: 'Automatically assign risk levels to vendors',
              },
            ].map((item) => (
              <label key={item.label} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked
                  className="mt-1 w-4 h-4 bg-slate-900/50 border border-slate-700/50 rounded text-cyan-500 focus:ring-cyan-500/50"
                />
                <div>
                  <p className="text-sm font-medium text-white">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Security */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Security</h2>
              <p className="text-sm text-slate-400">Manage security settings</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-white">Two-Factor Authentication</p>
                <p className="text-xs text-slate-500">Add an extra layer of security</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Enabled
              </span>
            </div>

            <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-white">API Access</p>
                <p className="text-xs text-slate-500">Manage API keys and webhooks</p>
              </div>
              <button className="text-xs bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1 rounded transition-all">
                Configure
              </button>
            </div>

            <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-white">Audit Logs</p>
                <p className="text-xs text-slate-500">View system activity history</p>
              </div>
              <button className="text-xs bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1 rounded transition-all">
                View Logs
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all">
            Save Changes
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
